import { useEffect, useRef, useState } from 'react'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { SxProps, Theme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'

const WAVEFORM_HEIGHT = 48
const SILENCE_TIMEOUT_MS = 3000
const VOLUME_THRESHOLD = 10 // out of 128 (center is 128 in byte time-domain data)
const COLOR_ACTIVE = '#4CAF50'
const COLOR_SILENT = '#BDBDBD'
const COLOR_ERROR = '#E57373'

type DeviceStatus = 'loading' | 'active' | 'error' | 'disconnected'

export type AudioDeviceWaveformProps = {
  deviceId: string
  sx?: SxProps<Theme>
}

export function AudioDeviceWaveform({ deviceId, sx = [] }: AudioDeviceWaveformProps) {
  const { t } = useTranslation()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [noSound, setNoSound] = useState(false)
  const [status, setStatus] = useState<DeviceStatus>('loading')

  useEffect(() => {
    let cancelled = false
    let rafId: number | undefined
    let stream: MediaStream | null = null
    let audioContext: AudioContext | null = null
    let silenceTimer: ReturnType<typeof setTimeout> | undefined

    // Circular buffer to store recent waveform slices for scrolling effect
    const bufferSize = 300
    const history: number[] = new Array(bufferSize).fill(128) // 128 = silence center
    let writeIndex = 0
    let lastSoundTime = Date.now()

    const startStream = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: deviceId } },
        })
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }

        if (!cancelled) setStatus('active')

        // Listen for track ending (device unplugged, permissions revoked)
        const track = stream.getAudioTracks()[0]
        if (track) {
          track.addEventListener('ended', () => {
            if (!cancelled) setStatus('disconnected')
          })
        }

        audioContext = new AudioContext()
        const analyser = audioContext.createAnalyser()
        analyser.fftSize = 256
        const source = audioContext.createMediaStreamSource(stream)
        source.connect(analyser)

        const dataArray = new Uint8Array(analyser.frequencyBinCount)

        // Check silence periodically
        silenceTimer = setInterval(() => {
          if (Date.now() - lastSoundTime > SILENCE_TIMEOUT_MS) {
            setNoSound(true)
          }
        }, 1000)

        const draw = () => {
          if (cancelled) return
          const canvas = canvasRef.current
          if (!canvas) {
            rafId = requestAnimationFrame(draw)
            return
          }

          analyser.getByteTimeDomainData(dataArray)

          // Compute peak deviation from center (128) for this frame
          let maxDeviation = 0
          for (let i = 0; i < dataArray.length; i++) {
            const deviation = Math.abs(dataArray[i] - 128)
            if (deviation > maxDeviation) maxDeviation = deviation
          }

          // Store the peak in circular buffer
          history[writeIndex] = maxDeviation
          writeIndex = (writeIndex + 1) % bufferSize

          const isActive = maxDeviation > VOLUME_THRESHOLD
          if (isActive) {
            lastSoundTime = Date.now()
            setNoSound(false)
          }

          // Draw scrolling waveform from history buffer
          const { width, height } = canvas
          const ctx = canvas.getContext('2d')
          if (!ctx) {
            rafId = requestAnimationFrame(draw)
            return
          }

          ctx.clearRect(0, 0, width, height)

          const barWidth = Math.max(1, width / bufferSize)
          const centerY = height / 2

          for (let i = 0; i < bufferSize; i++) {
            // Read from oldest to newest
            const idx = (writeIndex + i) % bufferSize
            const deviation = history[idx]
            const barActive = deviation > VOLUME_THRESHOLD
            ctx.fillStyle = barActive ? COLOR_ACTIVE : COLOR_SILENT

            const barHeight = Math.max(1, (deviation / 128) * height)
            const x = i * barWidth
            ctx.fillRect(x, centerY - barHeight / 2, Math.max(1, barWidth - 0.5), barHeight)
          }

          rafId = requestAnimationFrame(draw)
        }

        rafId = requestAnimationFrame(draw)
      } catch (err) {
        console.warn(`Could not access audio device ${deviceId}:`, err)
        if (!cancelled) setStatus('error')
      }
    }

    startStream()

    return () => {
      cancelled = true
      if (rafId !== undefined) cancelAnimationFrame(rafId)
      if (silenceTimer !== undefined) clearInterval(silenceTimer)
      stream?.getTracks().forEach((t) => t.stop())
      audioContext?.close()
    }
  }, [deviceId])

  const statusMessage = status === 'error'
    ? t('component.ui.deviceSelectionDialog.deviceUnavailable')
    : status === 'disconnected'
      ? t('component.ui.deviceSelectionDialog.deviceDisconnected')
      : noSound
        ? t('component.ui.deviceSelectionDialog.noSoundDetected')
        : null

  return (
    <Box sx={[{ position: 'relative' }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <Box
        component="canvas"
        ref={canvasRef}
        width={600}
        height={WAVEFORM_HEIGHT}
        sx={{
          width: '100%',
          height: `${WAVEFORM_HEIGHT}px`,
          borderRadius: '4px',
          backgroundColor: 'rgba(0,0,0,0.04)',
          ...(status === 'error' || status === 'disconnected' ? { border: `1px solid ${COLOR_ERROR}` } : {}),
        }}
      />
      {statusMessage && (
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            color: status === 'error' || status === 'disconnected' ? 'error.main' : 'text.secondary',
            fontSize: '11px',
            opacity: 0.7,
          }}
        >
          {statusMessage}
        </Typography>
      )}
    </Box>
  )
}

export default AudioDeviceWaveform
